import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import {
  SiteSettingsService,
  SocialLink,
  SocialSettings
} from '../../../../services/site-setting.service';

import { ToastService } from '../../../../services/toast.service';

interface SocialLinkForm {
  platform: string;
  customPlatform: string;
  url: string;
}

@Component({
  selector: 'app-admin-social',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  templateUrl: './admin-social.component.html',
  styleUrls: [
    './admin-social.component.css',
    '../../admin-section.shared.css'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminSocialComponent implements OnInit {

  readonly socialPlatforms = [
    'Facebook',
    'Instagram',
    'TikTok',
    'YouTube',
    'X',
    'Threads',
    'LinkedIn',
    'Snapchat',
    'Pinterest',
    'Bluesky',
    'Reddit',
    'Twitch',
    'Discord',
    'WhatsApp',
    'Nextdoor',
    'Yelp',
    'GoogleBusiness',
    'Other'
  ];

  socialLinks: SocialLinkForm[] = [];

  loading = true;
  saving = false;

  constructor(
    private readonly siteSettingsService: SiteSettingsService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadSocialSettings();
  }

  // ============================================================
  // LOAD
  // ============================================================

  loadSocialSettings(): void {
    this.loading = true;

    this.siteSettingsService
      .getSocialSettings()
      .subscribe({
        next: settings => {
          this.socialLinks = (settings.links ?? []).map(link => {
            const knownPlatform = this.socialPlatforms.find(platform =>
              platform !== 'Other' &&
              platform.toLowerCase() ===
                link.platform.trim().toLowerCase()
            );

            return {
              platform: knownPlatform ?? 'Other',
              customPlatform: knownPlatform
                ? ''
                : link.platform.trim(),
              url: link.url ?? ''
            };
          });

          this.loading = false;
          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to load social media settings:',
            error
          );

          this.loading = false;

          this.toastService.error(
            'Unable to load social media settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  // ============================================================
  // LINKS
  // ============================================================

  addSocialLink(): void {
    const nextPlatform =
      this.getNextAvailablePlatform();

    this.socialLinks = [
      ...this.socialLinks,
      {
        platform: nextPlatform,
        customPlatform: '',
        url: ''
      }
    ];

    this.changeDetector.markForCheck();
  }

  removeSocialLink(index: number): void {
    this.socialLinks =
      this.socialLinks.filter(
        (_, currentIndex) =>
          currentIndex !== index
      );

    this.changeDetector.markForCheck();
  }

  isPlatformUsed(
    platform: string,
    currentIndex: number
  ): boolean {
    if (platform === 'Other') {
      return false;
    }

    return this.socialLinks.some(
      (link, index) =>
        index !== currentIndex &&
        link.platform === platform
    );
  }

  displayPlatformName(
    platform: string
  ): string {
    if (platform === 'GoogleBusiness') {
      return 'Google Business';
    }

    return platform;
  }

  // ============================================================
  // SAVE
  // ============================================================

  saveSocialSettings(): void {
    const links: SocialLink[] =
      this.socialLinks.map(link => ({
        platform:
          link.platform === 'Other'
            ? link.customPlatform.trim()
            : link.platform.trim(),
        url: link.url.trim()
      }));

    const incompleteLink =
      links.some(link =>
        !link.platform ||
        !link.url
      );

    if (incompleteLink) {
      this.toastService.error(
        'Complete the platform and URL for every social media link.'
      );

      return;
    }

    if (
      links.some(link =>
        !this.isValidUrl(link.url)
      )
    ) {
      this.toastService.error(
        'Enter valid social media URLs beginning with http:// or https://.'
      );

      return;
    }

    const normalizedPlatforms =
      links.map(link =>
        link.platform.toLowerCase()
      );

    if (
      new Set(normalizedPlatforms).size !==
      normalizedPlatforms.length
    ) {
      this.toastService.error(
        'Each social media platform can only be added once.'
      );

      return;
    }

    this.saving = true;

    const settings: SocialSettings = {
      links
    };

    this.siteSettingsService
      .updateSocialSettings(settings)
      .subscribe({
        next: response => {
          this.saving = false;

          this.toastService.success(
            response.message
          );

          this.changeDetector.markForCheck();
        },
        error: error => {
          console.error(
            'Unable to save social media settings:',
            error
          );

          this.saving = false;

          this.toastService.error(
            error.error?.message ??
              'Unable to save social media settings.'
          );

          this.changeDetector.markForCheck();
        }
      });
  }

  // ============================================================
  // HELPERS
  // ============================================================

  private getNextAvailablePlatform():
    string {
    const availablePlatform =
      this.socialPlatforms.find(platform =>
        platform !== 'Other' &&
        !this.socialLinks.some(link =>
          link.platform === platform
        )
      );

    return availablePlatform ?? 'Other';
  }

  private isValidUrl(
    url: string
  ): boolean {
    try {
      const parsedUrl =
        new URL(url.trim());

      return (
        parsedUrl.protocol === 'http:' ||
        parsedUrl.protocol === 'https:'
      );
    } catch {
      return false;
    }
  }
}